import React, { useRef } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';

import {
  CONTENTFUL_INPUT_TYPES,
  INPUT_LAYOUT,
} from '../../../shared/constants';
import { IImage, IMdBody } from '../../../types/shared';

import { DEQ_TYPES } from '../../../utils/deq';
import ReactMarkdownExtended from '../../../utils/generic/ReactMarkdownExtended';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { ButtonLabel } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';
import { Theme } from '../../../styles/themes';

import InfoPopover from '../info-popover/InfoPopover';

interface Props {
  id: string;
  name: string;
  isRequired?: boolean;
  isChecked: boolean;
  value?: string;
  label: string;
  onInvalidHandler?: (
    validity: ValidityState,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLInputElement>
      | React.FormEvent<HTMLInputElement>,
    customError?: string,
    type?: string
  ) => void;
  error?: string;
  tooltip?: IMdBody | null;
  image?: IImage;
  layout?: string;
  submitOnClick?: boolean;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  hideRadioCircle?: boolean;
}

const EnrichedRadiobutton = ({
  id,
  name,
  isRequired,
  isChecked,
  value,
  label,
  error,
  tooltip,
  image,
  layout,
  onInvalidHandler,
  submitOnClick,
  onClick,
  hideRadioCircle,
}: Props) => {
  const radioRef = useRef<HTMLInputElement>(null);

  const handleOnChange = (
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLInputElement>
      | React.FormEvent<HTMLInputElement>
  ) => {
    if (radioRef?.current && onInvalidHandler)
      onInvalidHandler(
        radioRef.current?.validity,
        event,
        undefined,
        CONTENTFUL_INPUT_TYPES.radio
      );
  };

  return (
    <Container layout={layout} buttonSquared={hideRadioCircle}>
      <StyledLabel
        className="radiobutton"
        data-testid="form-radio"
        htmlFor={id}
        hasImage={!!image}
        checked={isChecked}
        layout={layout}
        noPadding={hideRadioCircle}
      >
        <RadioWrapper layout={layout} alignCenter={hideRadioCircle}>
          <StyledInput
            id={id}
            data-testid={`radio-${label}`.toLowerCase()}
            name={name}
            value={value}
            error={error}
            required={isRequired}
            type="radio"
            ref={radioRef}
            checked={isChecked}
            layout={layout}
            onChange={(event) => {
              if (!submitOnClick) {
                handleOnChange(event);
              }
            }}
            onInvalid={(event) => {
              event.preventDefault();
              handleOnChange();
            }}
            onClick={(event) => {
              if (submitOnClick) {
                handleOnChange(event);
              }
              if (onClick) {
                onClick(event);
              }
            }}
            hideCircleIcon={hideRadioCircle}
          />
          <IconLabelWrapper layout={layout}>
            {image?.gatsbyImageData && (
              <ImageWrapper layout={layout}>
                <GatsbyImage alt="" image={image.gatsbyImageData} />
              </ImageWrapper>
            )}
            {image?.file && (
              <ImageWrapper layout={layout}>
                <Image alt="" src={image.file.url} />
              </ImageWrapper>
            )}
            <span>{label}</span>
          </IconLabelWrapper>
        </RadioWrapper>
      </StyledLabel>
      {tooltip && (
        <InfoPopover label={label}>
          <ReactMarkdownExtended>
            {tooltip.childMarkdownRemark?.rawMarkdownBody}
          </ReactMarkdownExtended>
        </InfoPopover>
      )}
    </Container>
  );
};

export default EnrichedRadiobutton;

const Container = styled.div<{ layout?: string; buttonSquared?: boolean }>`
  align-items: stretch;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  height: ${({ buttonSquared }) => (buttonSquared ? '64px' : 'initial')};
  justify-content: space-between;
  min-width: ${({ layout, buttonSquared }) =>
    layout === INPUT_LAYOUT.stacked && !buttonSquared
      ? 'initial'
      : buttonSquared
      ? 'auto'
      : '164px'};
  width: ${({ buttonSquared }) => (buttonSquared ? '40px' : '100%')};

  ${mq.from.breakpoint.M`
    height: ${({ buttonSquared }: { buttonSquared?: boolean }) =>
      buttonSquared ? '40px' : 'initial'};
  `}
`;

const hideInput = css`
  border: none;
  height: 0;
  left: 0;
  max-width: 0;
  opacity: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: 0;
`;

const StyledInput = styled.input<{
  error?: string;
  layout?: string;
  hideCircleIcon?: boolean;
}>`
  appearance: none;
  background-color: ${Colors.white};
  border: 1px solid ${Colors.abnNeutralsN100};
  border-radius: ${({ theme }) => theme.radiuses.XXL};
  display: grid;
  font: inherit;
  height: 1.15em;
  margin: 0;
  margin-right: 12px;
  padding: ${Spacings.XS};
  place-content: center;
  width: 1.15em;

  &:before {
    border-radius: ${({ theme }) => theme.radiuses.S};
    box-shadow: inset 16px 16px ${({ theme }) => theme.colors.abnGreen200};
    content: '';
    height: 0.65em;
    transform: scale(0);
    transition: 0.1s transform ease-in-out;
    width: 0.65em;
  }

  ${({ checked }) =>
    checked
      ? `border: 1px solid ${({ theme }) => theme.colors.abnGreen200};

    &:before {
      transform: scale(1);
    }`
      : ''} {
  }

  ${mq.from.breakpoint.M`
    margin-right: ${({ layout }: { layout?: string }) =>
      layout === INPUT_LAYOUT.stacked ? '12px' : '0'};
  `}

  ${({ layout }: { layout?: string }) =>
    layout !== INPUT_LAYOUT.stacked
      ? css`
          ${mq.from.breakpoint.M`
        position: absolute;
        top: 0;
        left: 0;
      `}
        `
      : null}

  ${({ hideCircleIcon }) => hideCircleIcon && hideInput}
`;

const StyledLabel = styled.label<{
  checked: boolean;
  error?: string;
  hasImage: boolean;
  layout?: string;
  noPadding?: boolean;
}>`
  background-color: ${({ checked }) =>
    checked ? Colors.white : Colors.abnGreen50};
  border: 2px solid
    ${({ checked, error, theme }) =>
      checked
        ? theme.colors.abnGreen300
        : error
        ? Colors.abnNegative400
        : Colors.abnGreen50};
  border-radius: ${({ theme }) => theme.radiuses.S};
  display: flex;
  font-family: ${({ theme }) => theme.fontRegular};
  ${ButtonLabel}
  justify-content: space-between;
  padding: ${({ hasImage, layout, noPadding }) =>
    hasImage && !noPadding && layout === INPUT_LAYOUT.stacked
      ? `${Spacings.XS} 12px ${Spacings.XS} ${Spacings.S}`
      : noPadding
      ? '0px'
      : `12px 12px 12px ${Spacings.S}`};
  width: 100%;

  ${mq.from.breakpoint.XL`
    &:hover {
      cursor: pointer;
      background-color: ${Colors.abnGreen55};
      border: 2px solid ${({
        checked,
        theme,
      }: {
        checked: boolean;
        theme: Theme;
      }) => (checked ? theme.colors.abnGreen300 : Colors.abnGreen75)};
        
      input[type='radio'] {
          border: 1px solid ${({
            checked,
            theme,
          }: {
            checked: boolean;
            theme: Theme;
          }) => (checked ? theme.colors.abnGreen300 : Colors.abnGreen75)};
          background-color: transparent;
          cursor: pointer;

          &:before {
            box-shadow: inset 16px 16px ${({ theme }) =>
              theme.colors.abnGreen300};
          }
        }
    }
  `}

  &:active {
    background-color: ${Colors.abnGreen65};
    border: 2px solid ${({ theme }) => theme.colors.abnGreen100};
  }
`;

const RadioWrapper = styled.div<{ layout?: string; alignCenter?: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: ${({ alignCenter }) =>
    alignCenter ? 'center' : 'flex-start'};
  position: relative;
  width: 100%;

  ${mq.from.breakpoint.M`
    justify-content: ${({ layout }: { layout: string }) =>
      layout === INPUT_LAYOUT.stacked ? 'flex-start' : 'center'};
  `}
`;

const IconLabelWrapper = styled.div<{ layout?: string }>`
  align-items: center;
  display: flex;
  flex-direction: row;

  ${mq.from.breakpoint.M`
    flex-direction: ${({ layout }: { layout: string }) =>
      layout === INPUT_LAYOUT.stacked ? 'row' : 'column'};
    text-align: ${({ layout }: { layout: string }) =>
      layout === INPUT_LAYOUT.stacked ? 'left' : 'center'};
  `}
`;

const Image = styled.img`
  width: 100%;
`;

const ImageWrapper = styled.div<{ layout?: string }>`
  height: 48px;
  margin-right: ${Spacings.XS};
  min-width: 48px;
  width: 48px;

  ${mq.from.breakpoint.M`
      margin-right: ${({ layout }: { layout?: string }) =>
        layout === INPUT_LAYOUT.stacked ? Spacings.XS : '0'};
  `}
`;
